import React from 'react';
import { Box, Typography } from '@mui/material';
import { usePartnerDashboardSections, schoolRowsFrom, ATTENTION_STATUSES } from './partnerPortfolioData';

/**
 * Growth Opportunities > Portfolio Snapshot > Attention Needed — Partner
 * workspace, restricted to P0 and the whole P1 family (enterprise_p1,
 * enterprise_p1a, enterprise_p1b treated as one "P1" — not split by product
 * access) per the mockup's "P0, P1" tag; no LC, unlike its Executive
 * Overview sibling. See the route gate in PrivateRouteWithLayout.js and the
 * nav row's visibleWhen in partnerWorkspace.nav.js.
 *
 * Built from a reference mockup (a 9-row deployment table) with sample/mock
 * data — there is no portfolio-metrics API for this feature yet (see
 * PARTNER_WORKSPACE_PENDING in partnerWorkspace.nav.js). Rows are reproduced
 * verbatim, including the dev-environment test school names — that's the
 * actual content shown in the mockup, not invented. The table's row/zebra
 * striping match the other Growth Opportunities tables in this app; the
 * header row uses this app's own brand orange (a plain light-grey header
 * didn't read as a header, and the Partner Dashboard's navy chrome isn't
 * this app's own color) rather than a fresh palette. Status chips use the
 * exact solid fills from `DEPLOYMENT_ACTION_BAND_COLORS` (utils/
 * constants.js), the same convention every dashboard's deployment-status
 * chip already uses (e.g. PartnerDashboard/pages/AttentionNeeded).
 */

const BORDER = 'var(--brand-border, #E5E7EB)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const TEXT = 'var(--brand-text, #111827)';
const ORANGE = '#FF6900';

const COLS = '1.3fr 1fr 1fr 2fr 1.6fr';

// Same solid fills as DEPLOYMENT_ACTION_BAND_COLORS (utils/constants.js),
// used by every other dashboard's deployment-status chip (e.g.
// PartnerDashboard/pages/AttentionNeeded) — not a fresh palette.
const STATUS_STYLES = {
    low_coverage: { bg: '#E8730C', label: 'Low coverage' },
    data_weak: { bg: '#EFA020', label: 'Data weak' },
    dormant: { bg: '#9CA3AF', label: 'Dormant' },
    high_risk: { bg: '#E04535', label: 'High risk' },
};

// LA-PW-20Oct-02: the rows are the partner's own schools (Partner Dashboard
// S_8 By School) that need action, worded as its Attention Needed page words them.
const STATUS_KEY = { 'Low coverage': 'low_coverage', 'Data weak': 'data_weak', Dormant: 'dormant', 'High risk': 'high_risk' };

const ColHeader = ({ label }) => (
    <Typography sx={{
        fontSize: '12px', fontWeight: 700, color: '#ffffff',
        textTransform: 'uppercase', letterSpacing: '0.5px',
    }}>
        {label}
    </Typography>
);

const StatusChip = ({ status }) => {
    const style = STATUS_STYLES[status];
    return (
        <Box sx={{
            display: 'inline-block', backgroundColor: style.bg, color: '#ffffff',
            fontSize: '12px', fontWeight: 600, px: 1, py: 0.5,
            borderRadius: '20px', width: 'fit-content', textAlign: 'center', whiteSpace: 'nowrap',
        }}>
            {style.label}
        </Box>
    );
};

const PortfolioAttentionNeededView = ({ rows }) => (
    <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
        <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 1.5 }}>
            Attention Needed
        </Typography>
        <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.6, mb: 4 }}>
            Which deployments need immediate partner action?
        </Typography>

        <Box sx={{
            backgroundColor: 'var(--brand-surface, #ffffff)', borderRadius: '16px',
            border: `1px solid ${BORDER}`, p: { xs: 2, sm: '20px 24px' },
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        }}>
            <Box sx={{ minWidth: '920px' }}>
                <Box sx={{
                    display: 'grid', gridTemplateColumns: COLS, gap: 2, px: 2.5, py: 2,
                    backgroundColor: ORANGE, borderRadius: '10px', mb: 0.5,
                }}>
                    <ColHeader label="School" />
                    <ColHeader label="Country" />
                    <ColHeader label="Status" />
                    <ColHeader label="What is happening" />
                    <ColHeader label="Recommended action" />
                </Box>

                {rows.map((row, i) => (
                    <Box
                        key={`${row.school}-${row.country}`}
                        sx={{
                            display: 'grid', gridTemplateColumns: COLS, gap: 2, alignItems: 'center',
                            px: 2, py: 1.75, borderRadius: '8px',
                            backgroundColor: i % 2 !== 0 ? 'var(--brand-bg, #F9FAFB)' : 'var(--brand-surface, #ffffff)',
                            '&:hover': { backgroundColor: 'var(--brand-bg, #F3F4F6)' },
                        }}
                    >
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>
                            {row.school}
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: MUTED }}>{row.country}</Typography>
                        <StatusChip status={row.status} />
                        <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.5 }}>
                            {row.whatIsHappening}
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.5 }}>
                            {row.recommendedAction}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

const PortfolioAttentionNeededPage = () => {
    const { data } = usePartnerDashboardSections(['S_8']);
    const rows = schoolRowsFrom(data)
        .filter((r) => ATTENTION_STATUSES.includes(r.status))
        .map((r) => ({
            school: r.client,
            country: r.country,
            status: STATUS_KEY[r.status],
            whatIsHappening: r.happening,
            recommendedAction: r.action,
        }));
    return <PortfolioAttentionNeededView rows={rows} />;
};

export default PortfolioAttentionNeededPage;
